import React, { useState, useMemo, useEffect } from "react";
import { DatePicker, Input, Dropdown } from "antd";
import MainLayout from "../../layout/MainLayout";
import {
  Button1,
  Button2,
  Checkbox,
  ProgressBar,
  SmallCircle,
  QuickActionSelect,
} from "../index";
import searchIcon from "../../assets/icons/search-icon.png";
import importIcon from "../../assets/icons/import-icon.png";
import settingIcon from "../../assets/icons/settings-icon-green.png";
import sortIcon from "../../assets/icons/sort-icon.png";
import Table from "../table/Table";
import bankIcon from "../../assets/images/bank-icon.png";
import { useNavigate } from "react-router-dom";
import { VerticalBar } from "../vertical-bar/VerticalBar";

import { useAccounts, computeTotalBalance, useTransactions } from "../../data/live";

const SHORT_MONTHS = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];

/**
 * Build the Overview bar chart from real transactions:
 *   Assets      = monthly credits (money in)
 *   Liabilities = monthly debits  (money out)
 *   Net Worth   = Assets − Liabilities
 * Last 4 months ending at current month.
 */
const buildOverviewChart = (transactions, accounts) => {
  const now = new Date();
  const buckets = [];
  for (let i = 3; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    buckets.push({
      label: SHORT_MONTHS[d.getMonth()],
      year: d.getFullYear(),
      month: d.getMonth(),
      assets: 0,
      liabilities: 0,
    });
  }
  for (const t of transactions || []) {
    const d = new Date(t.raw?.createdAt || t.date);
    const bucket = buckets.find((b) => b.year === d.getFullYear() && b.month === d.getMonth());
    if (!bucket) continue;
    const amt = Number(t.amount || 0);
    if (t.direction === "in") bucket.assets += amt;
    else if (t.direction === "out") bucket.liabilities += amt;
  }
  // For the most recent month, blend in current account balance as "Assets" floor
  // so a brand-new wallet with deposits but no transactions shows non-zero.
  const currentBalance = (accounts || []).reduce(
    (s, a) => s + Number(a.availableBalance || 0),
    0
  );
  if (buckets.length > 0 && buckets[buckets.length - 1].assets === 0) {
    buckets[buckets.length - 1].assets = currentBalance;
  }
  return {
    labels: buckets.map((b) => b.label),
    datasets: [
      {
        label: "Assets",
        data: buckets.map((b) => b.assets),
        backgroundColor: "#1bbb6b",
        borderRadius: 4,
        borderSkipped: false,
      },
      {
        label: "Liabilities",
        data: buckets.map((b) => b.liabilities),
        backgroundColor: "#FC3C42",
        borderRadius: 4,
        borderSkipped: false,
      },
      {
        label: "Net Worth",
        data: buckets.map((b) => Math.max(0, b.assets - b.liabilities)),
        backgroundColor: "#F7A64F",
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  };
};

const MyAccounts = () => {
  const navigate = useNavigate();
  const { accounts } = useAccounts();
  const { items: transactions } = useTransactions();
  const overviewChart = useMemo(
    () => buildOverviewChart(transactions, accounts),
    [transactions, accounts]
  );

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("balance-desc");
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const sortMenu = {
    items: [
      { key: "balance-desc", label: "Balance, high to low" },
      { key: "balance-asc", label: "Balance, low to high" },
      { key: "currency-asc", label: "Currency, A to Z" },
      { key: "currency-desc", label: "Currency, Z to A" },
      { key: "default-first", label: "Default wallet first" },
    ],
    onClick: ({ key }) => setSortBy(key),
  };

  const filteredAndSortedAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = accounts.filter((a) => {
      if (!q) return true;
      return (
        (a.currency || "").toLowerCase().includes(q) ||
        (a.iban || "").toLowerCase().includes(q) ||
        (a.accountNumber || "").toLowerCase().includes(q) ||
        (a.category || "").toLowerCase().includes(q)
      );
    });
    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case "balance-asc":
          return Number(a.availableBalance) - Number(b.availableBalance);
        case "balance-desc":
          return Number(b.availableBalance) - Number(a.availableBalance);
        case "currency-asc":
          return (a.currency || "").localeCompare(b.currency || "");
        case "currency-desc":
          return (b.currency || "").localeCompare(a.currency || "");
        case "default-first":
          return (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0);
        default:
          return 0;
      }
    });
    return list;
  }, [accounts, search, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedAccounts.length / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortBy, pageSize]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const paginatedAccounts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAndSortedAccounts.slice(start, start + pageSize);
  }, [currentPage, pageSize, filteredAndSortedAccounts]);

  const pageAccountIds = paginatedAccounts.map((account) => account.id);
  const allSelected =
    pageAccountIds.length > 0 &&
    pageAccountIds.every((id) => selectedAccountIds.includes(id));
  const someSelected = selectedAccountIds.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedAccountIds((prev) => {
      if (allSelected) {
        return prev.filter((id) => !pageAccountIds.includes(id));
      }
      return Array.from(new Set([...prev, ...pageAccountIds]));
    });
  };

  const toggleOne = (id) => {
    setSelectedAccountIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const firstResult =
    filteredAndSortedAccounts.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastResult = Math.min(currentPage * pageSize, filteredAndSortedAccounts.length);

  const allTableHeader = [
    <Checkbox
      checked={allSelected}
      indeterminate={someSelected}
      onChange={toggleAll}
    />,
    "From",
    "Category",
    "Tags",
    "Last Refresh",
    "Amount",
    "",
  ];

  const bankAccountTableBody = paginatedAccounts.map((a) => ({
    id: a.id,
    selected: selectedAccountIds.includes(a.id),
    onToggle: () => toggleOne(a.id),
    from: {
      icon: bankIcon,
      name: a.currency + " Wallet",
      info: a.iban ? `••••••${a.iban.slice(-4)}` : "Digital Wallet",
    },
    category: "General",
    tags: ["Live"],
    lastRefresh: "Just now",
    amount: `${Number(a.availableBalance).toLocaleString()} ${a.currency}`,
  }));

  const { total } = computeTotalBalance(accounts, "AUD");

  const allSections = [
    {
      sectionName: "Bank Accounts",
      tableDetails: {
        tableHeaders: allTableHeader,
        tableBody: bankAccountTableBody,
      },
      subTotal: `${total.toLocaleString()} AUD`,
    },
  ];

  return (
    <>
      <MainLayout
        expand={false}
        NavName="Bank Account"
        pageClassName="dashboard-page my-account-page"
        contentClassName="dashboard-content"
      >
        <div className="my-account-shell">
          <div className="my-account-overview">
            <div>
              <span className="dashboard-eyebrow">Linked accounts</span>
              <h2>Bank Account</h2>
              <p>Review connected accounts, balances, and net worth activity in the same workspace style as Mobile Wallet.</p>
            </div>
          </div>

          <div className="my-account-toolbar mt-4">
            <div className="d-flex justify-content-lg-between gap-2 align-items-center flex-wrap flex-lg-nowrap justify-content-center">
              <span
                className="fs-5 fw-medium"
                style={{ minWidth: "max-content" }}
              >
                Account List
              </span>
              <div className="d-flex align-items-center justify-content-lg-end gap-2 w-100 flex-wrap flex-xl-nowrap justify-content-center">
                <Input
                  prefix={
                    <img src={searchIcon} alt="search" width={14} height={14} />
                  }
                  placeholder="Search"
                  className="rounded-pill w-auto"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  allowClear
                />
                <Dropdown menu={sortMenu} trigger={["click"]} placement="bottomRight">
                  <div>
                    <Button2
                      buttonName={"Sort"}
                      className={"border"}
                      icon={
                        <img src={sortIcon} alt="sort" width={16} height={16} />
                      }
                      divClassName={"gap-2"}
                    />
                  </div>
                </Dropdown>
                <Button2
                  buttonName={"Import Manually"}
                  className={"border"}
                  icon={
                    <img src={importIcon} alt="sort" width={16} height={16} />
                  }
                  divClassName={"gap-2"}
                />
                <Button2
                  buttonName={"Manage Connected Services"}
                  className={"btn-outline-success"}
                  icon={
                    <img src={settingIcon} alt="sort" width={16} height={16} />
                  }
                  divClassName={"gap-2"}
                />

                <QuickActionSelect />
              </div>
            </div>

            </div>

            <div className="row g-3 mt-3 justify-content-between ">
              <div className="col-lg-8 my-account-table-column">
                {allSections.map(
                  ({ sectionName, tableDetails, subTotal }, key) => (
                    <div
                      key={key}
                      className={`my-account-card d-flex flex-column ${
                        key !== 0 && "mt-4"
                      }`}
                    >
                      <span className="my-account-card-title border-bottom p-3 fs-5 fw-semibold">
                        {sectionName}
                      </span>

                      <div className="p-3">
                        <Table
                          MyAccountTable={true}
                          className={"w-100"}
                          tableBody={tableDetails.tableBody}
                          tableHeaders={tableDetails.tableHeaders}
                          keyNumber1={1}
                          condition1={"col-3"}
                          condition2={""}
                        />
                      </div>

                      {selectedAccountIds.length > 0 && (
                        <div className="my-account-selection-bar d-flex align-items-center justify-content-between mx-3 mb-3 px-3 py-2 rounded-3">
                          <span className="fs14px fw-medium">
                            {selectedAccountIds.length} selected
                          </span>
                          <Button1
                            buttonName="Clear"
                            className="border"
                            onClick={() => setSelectedAccountIds([])}
                          />
                        </div>
                      )}

                      <div className="transaction-pagination my-account-pagination mx-3 mb-3">
                        <span>
                          Showing {firstResult}-{lastResult} of {filteredAndSortedAccounts.length}
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          <select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            aria-label="Rows per page"
                          >
                            <option value={5}>5 / page</option>
                            <option value={10}>10 / page</option>
                            <option value={15}>15 / page</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                          <strong>
                            {currentPage} / {totalPages}
                          </strong>
                          <button
                            type="button"
                            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between p-3 align-items-center border-top">
                        <span className="fs14px">SubTotal</span>
                        <span
                          className="fw-semibold"
                          style={{ color: "#1bbb6b" }}
                        >
                          {subTotal}
                        </span>
                      </div>
                    </div>
                  )
                )}
              </div>

              <div className="col-lg-4 my-account-overview-card h-auto p-3 ">
                <div className="d-flex  flex-column justify-content-between h-100 gap-4">
                  <div className="d-flex flex-column gap-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="fs-5 fw-semibold">Overview</span>
                      <DatePicker
                        className="rounded-pill"
                        placeholder="custom date"
                      />
                    </div>
                    <div className="d-flex flex-column gap-2">
                      <ProgressBar
                        label={"Assets"}
                        labelClassName="fw-semibold fs13px"
                        percent={total > 0 ? 100 : 0}
                      />
                      <ProgressBar
                        label={"Liabilities"}
                        labelClassName="fw-semibold fs13px"
                        percent={0}
                        strokeColor="#FC555B"
                      />
                    </div>

                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <SmallCircle />
                          <span className="fs12px fw-medium">Assets</span>
                        </div>

                        <span
                          className="fs12px fw-bold"
                          style={{ color: "#1bbb6b" }}
                        >
                          {total.toLocaleString()} AUD
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <SmallCircle color="#FC555B" />
                          <span className="fs12px fw-medium">Liabilities</span>
                        </div>

                        <span
                          className="fs12px fw-bold"
                          style={{ color: "#FC555B" }}
                        >
                          0 AUD
                        </span>
                      </div>

                      <hr className="w-100 align-self-center opacity-25"></hr>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fs12px fw-medium">Net Worth</span>

                        <span
                          className="fs12px fw-bold"
                          style={{ color: "#1bbb6b" }}
                        >
                          {total.toLocaleString()} AUD
                        </span>
                      </div>
                    </div>

                    <div className="h-100 mt-2 ">
                      <VerticalBar
                        yGrid={false}
                        label={overviewChart.labels}
                        barChartData={overviewChart.datasets}
                      />
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-3 w-100">
                    <Button1
                      buttonName={"Connect New Account"}
                      className={"btn-dark"}
                      onClick={() => navigate("/connect-new-account")}
                    />


                    <Button1
                      buttonName={"Manually Add New Account"}
                      className={"btn-outline-success"}
                      onClick={() => navigate("/connect-new-account")}
                    />
                    <Button1
                      buttonName={"Refresh All accounts"}
                      className={"border"}
                      onClick={() => window.location.reload()}
                    />
                  </div>
                </div>
              </div>
          </div>
        </div>
      </MainLayout>
    </>
  );
};

export default MyAccounts;
